import { useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { Home, FileText, BarChart3, CheckCircle2, Circle, Check } from 'lucide-react'
import { OFFERTE_STATUS, offerteStatus, isOfferteStatus } from '../utils/offerteStatus'

const portaalApi = axios.create({ baseURL: '/api/v1/klantenportaal', timeout: 10000 })

const STATUS_CFG = {
  [OFFERTE_STATUS.CONCEPT]: { variant: 'badge-concept', label: 'Concept' },
  [OFFERTE_STATUS.VERZONDEN]: { variant: 'badge-warning', label: 'Wacht op goedkeuring' },
  [OFFERTE_STATUS.GEACCEPTEERD]: { variant: 'badge-success', label: 'Goedgekeurd' },
  [OFFERTE_STATUS.GEWEIGERD]: { variant: 'badge-danger', label: 'Geweigerd' },
}

export default function KlantenPortaalPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [tab, setTab] = useState('offertes')
  const [signMode, setSignMode] = useState(false)
  const [signOfferteId, setSignOfferteId] = useState(null)
  const canvasRef = useRef(null)
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portaal', token],
    queryFn: () => portaalApi.get(`/portaal/${token}`).then(r => r.data),
    enabled: !!token,
    retry: false,
  })

  const goedkeurMut = useMutation({
    mutationFn: ({ offerteId, handtekening }) =>
      portaalApi.post(`/portaal/${token}/keur-offerte-goed/${offerteId}`, null, { params: { handtekening } }),
    onSuccess: () => { qc.invalidateQueries(['portaal', token]); setSignMode(false); alert('Offerte goedgekeurd!') },
    onError: (e) => alert('Fout: ' + (e.response?.data?.detail || e.message)),
  })

  const startDraw = (e) => {
    const c = canvasRef.current; if (!c) return
    const ctx = c.getContext('2d'), r = c.getBoundingClientRect()
    const x = (e.clientX || e.touches?.[0]?.clientX) - r.left
    const y = (e.clientY || e.touches?.[0]?.clientY) - r.top
    ctx.beginPath(); ctx.moveTo(x, y); c._drawing = true
  }
  const draw = (e) => {
    const c = canvasRef.current; if (!c?._drawing) return
    const ctx = c.getContext('2d'), r = c.getBoundingClientRect()
    ctx.lineTo((e.clientX || e.touches?.[0]?.clientX) - r.left, (e.clientY || e.touches?.[0]?.clientY) - r.top)
    ctx.strokeStyle = '#1A3A5C'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.stroke()
  }
  const stopDraw = () => { if (canvasRef.current) canvasRef.current._drawing = false }

  if (!token) return <div className="min-h-screen bg-canvas flex items-center justify-center p-6"><div className="r11-card r11-card-pad dark:bg-gray-800 dark:border-gray-700 text-center max-w-md"><h2 className="text-xl font-bold text-ink mb-2">Geen toegangslink</h2><p className="text-muted">Gebruik de link uit uw e-mail.</p></div></div>
  if (isLoading) return <div className="min-h-screen bg-canvas flex items-center justify-center"><p className="text-muted">Laden...</p></div>
  if (error) return <div className="min-h-screen bg-canvas flex items-center justify-center p-6"><div className="r11-card r11-card-pad dark:bg-gray-800 dark:border-gray-700 text-center max-w-md"><h2 className="text-xl font-bold text-red-600 mb-2">Ongeldige of verlopen link</h2><p className="text-muted">Neem contact op met uw verkoper.</p></div></div>

  return (
    <div className="min-h-screen bg-canvas" style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
      <div className="bg-[#1A3A5C] text-white px-8 py-5">
        <div className="max-w-3xl mx-auto flex justify-between items-center">
          <div><h1 className="text-xl font-bold m-0">Mijn Keukenproject</h1><p className="text-sm opacity-80 mt-1">Welkom, {data?.klant?.naam}</p></div>
          <Home className="w-7 h-7 opacity-80" />
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-5 border-b border-line">
          {[['offertes', 'Offertes', FileText], ['status', 'Status', BarChart3]].map(([id, l, Icon]) => (
            <button key={id} onClick={() => setTab(id)} className={`r11-tab inline-flex items-center gap-2 ${tab === id ? 'r11-tab--active' : ''}`}><Icon className="w-4 h-4" />{l}</button>
          ))}
        </div>

        {tab === 'offertes' && (data?.offertes || []).map(o => {
          const cfg = STATUS_CFG[offerteStatus(o.status)] || STATUS_CFG[OFFERTE_STATUS.CONCEPT]
          return (
            <div key={o.id} className="r11-card r11-card-pad dark:bg-gray-800 dark:border-gray-700 mb-4">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-lg text-[#1A3A5C] m-0">Offerte {o.nummer}</h3>
                  <p className="text-sm text-muted mt-1">{o.datum ? new Date(o.datum).toLocaleDateString('nl-BE') : ''}{o.geldig_tot ? ` · Geldig tot ${new Date(o.geldig_tot).toLocaleDateString('nl-BE')}` : ''}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#1A3A5C]">€{(o.totaal_incl || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</div>
                  <span className={`badge ${cfg.variant} mt-1`}>{cfg.label}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                {data.rechten?.includes('offerte_goedkeuren') && isOfferteStatus(o.status, OFFERTE_STATUS.VERZONDEN) && (
                  <button onClick={() => { setSignOfferteId(o.id); setSignMode(true) }} className="btn-primary"><Check className="w-4 h-4" />Goedkeuren</button>
                )}
              </div>
            </div>
          )
        })}

        {tab === 'status' && (
          <div className="r11-card r11-card-pad dark:bg-gray-800 dark:border-gray-700">
            <h3 className="font-bold text-ink mb-4">Projectvoortgang</h3>
            {['Offerte ontvangen', 'Goedkeuring', 'Productie', 'Levering', 'Montage'].map((s, i) => (
              <div key={s} className="flex items-center gap-3 py-3 border-b border-line dark:border-gray-700 last:border-0">
                {i === 0
                  ? <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0" />
                  : <Circle className="w-5 h-5 text-faint shrink-0" />}
                <span className={i === 0 ? 'font-bold text-green-700' : 'text-muted'}>{s}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {signMode && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface dark:bg-gray-800 rounded-card p-7 max-w-lg w-full shadow-xl">
            <h3 className="font-bold text-lg text-[#1A3A5C] mb-2">Digitaal ondertekenen</h3>
            <p className="text-sm text-muted mb-4">Teken hieronder ter bevestiging van de offerte.</p>
            <canvas ref={canvasRef} width={420} height={160} className="border-2 border-[#1A3A5C] rounded-control w-full cursor-crosshair" style={{ touchAction: 'none' }}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw} />
            <div className="flex gap-2 mt-4">
              <button onClick={() => canvasRef.current?.getContext('2d').clearRect(0, 0, 420, 160)} className="btn-ghost">Wissen</button>
              <button onClick={() => setSignMode(false)} className="btn-ghost">Annuleren</button>
              <button onClick={() => goedkeurMut.mutate({ offerteId: signOfferteId, handtekening: canvasRef.current?.toDataURL('image/png') })}
                disabled={goedkeurMut.isPending} className="btn-primary flex-1 disabled:opacity-50">
                {goedkeurMut.isPending ? 'Bezig...' : <><Check className="w-4 h-4" />Bevestig</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
